import { useRef, useState } from 'react';
import buildServiceIntervalUpdates from 'utils/buildServiceIntervalUpdates';

import useAssetModels from 'hooks/useAssetModels';
import useIsAdmin from 'hooks/useIsAdmin';
import useServiceIntervals from 'hooks/useServiceIntervals';

import { FIRST_REQUEST } from './constants';
import type {
    OnSubmitServiceIntervals,
    PartsByAssetModelId,
    ServiceIntervalBatchResponse,
    UseData
} from './types';

const useData = (): UseData => {
    const { assetModels, fetch: fetchAssetModels } = useAssetModels();
    const { isAdmin } = useIsAdmin();
    const { fetchServiceIntervals, updateServiceIntervals } = useServiceIntervals();

    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [partsByAssetModelId, setPartsByAssetModelId] = useState<PartsByAssetModelId>({});
    const [unavailableAssetModelIds, setUnavailableAssetModelIds] = useState<string[]>([]);

    const requestRef = useRef(FIRST_REQUEST);

    const handleOpen = async () => {
        requestRef.current += 1;

        const request = requestRef.current;
        const isCurrentRequest = () => requestRef.current === request;

        setIsOpen(true);
        setPartsByAssetModelId({});
        setUnavailableAssetModelIds([]);

        try {
            setIsLoading(true);

            const models = await fetchAssetModels();

            if (!models?.length || !isCurrentRequest()) {
                return;
            }

            const loadedParts: PartsByAssetModelId = {};
            const unavailable: string[] = [];

            for (const { id } of models) {
                const batch = await fetchServiceIntervals({ assetModelId: id });

                if (batch) {
                    loadedParts[batch.assetModelId] = batch.parts;
                } else {
                    unavailable.push(id);
                }
            }

            if (!isCurrentRequest()) {
                return;
            }

            setPartsByAssetModelId(loadedParts);
            setUnavailableAssetModelIds(unavailable);
        } finally {
            if (isCurrentRequest()) {
                setIsLoading(false);
            }
        }
    };

    const handleClose = () => setIsOpen(false);

    const handleSubmit: OnSubmitServiceIntervals = async ({ intervals, units }) => {
        const batches = Object.entries(partsByAssetModelId)
            .map(([assetModelId, parts]) => ({
                assetModelId,
                intervals: buildServiceIntervalUpdates({
                    intervalUnits: units,
                    intervalValues: intervals,
                    parts
                })
            }))
            .filter(({ intervals: updates }) => updates.length > 0);

        if (!batches.length) {
            setIsOpen(false);

            return;
        }

        try {
            setIsSaving(true);

            const saved: ServiceIntervalBatchResponse[] = [];

            for (const batch of batches) {
                const result = await updateServiceIntervals(batch);

                if (result) {
                    saved.push(result);
                }
            }

            setPartsByAssetModelId(current =>
                saved.reduce(
                    (parts, batch) => ({ ...parts, [batch.assetModelId]: batch.parts }),
                    current
                )
            );

            if (saved.length === batches.length) {
                setIsOpen(false);
            }
        } finally {
            setIsSaving(false);
        }
    };

    return {
        assetModels,
        isEditable: isAdmin,
        isLoading,
        isOpen,
        isSaving,
        onClose: handleClose,
        onOpen: handleOpen,
        onSubmit: handleSubmit,
        partsByAssetModelId,
        unavailableAssetModelIds
    };
};

export default useData;
